<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import { Initiative } from '@/models/initiative.model';
import { UserLabel } from '@/models/label.model';
import { MediaPurpose } from '@/models/media.model';
import {
  Story,
  StoryType,
  titleRules,
  notificationCTARules,
} from '@/models/story.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import { LabelsState } from '@/stores/labels.store';
import { StoriesActions, StoriesState } from '@/stores/stories.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import CategoryField from '../categories/CategoryField.vue';
import CategoriesField from '../categories/CategoriesField.vue';

const store = useStore<
  ShiftCMSState & {
    ts: StoriesState;
    ls: LabelsState;
    cns: ContentStoreState;
  }
>();
const events = useEvents();
const stories = computed<Story[]>(() => store.state.ts.stories);
const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);
const labels = computed<UserLabel[]>(() => store.state.ls.labels);

const props = defineProps({
  data: {
    type: Object as PropType<Story>,
  },
});

const valid = ref<boolean>(false);

const story = ref<Story>({
  id: NEW_ID,
  title: '',
  notificationCTA: '',
  type: StoryType.Quiz,
  index: stories.value.length,
  items: [],
  notRelevantForlabels: [],
  published: false,
  media: [],
  categories: [],
  initiativeOfTheDay: null,
});

const action = computed<string>(() =>
  story.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveStory = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (story.value.id === NEW_ID) {
      success = await store.dispatch(StoriesActions.AddStory, story.value);
    } else {
      success = await store.dispatch(StoriesActions.UpdateStory, story.value);
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved story',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(StoriesActions.GetStories);
  }
};

const updateLanguage = async () => {
  if (story.value.id === NEW_ID) {
    Object.assign(story.value, { title: '', type: 'story' });
  } else {
    const cat = await store.dispatch(StoriesActions.GetStory, story.value.id);

    if (cat) {
      Object.assign(story.value, { ...cat });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  store.dispatch(ContentStoreActions.GetInitiatives);
  if (props.data) {
    Object.assign(story.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

const types = [
  {
    title: 'Quiz',
    value: 'quiz',
  },
  {
    title: 'Story',
    value: 'story',
  },
];
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveStory">
    <v-container>
      <h2>{{ $t(action) }} a story <CurrentLanguage /></h2>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="story.title"
            :rules="titleRules"
            label="Title"
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <CategoryField
            main
            v-model="story.mainCategory"
            disabled
            label="Main Category"
          />
        </v-col>

        <v-col class="mx-auto">
          <v-select
            v-model="story.type"
            :items="types"
            label="Type"
            required
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <CategoriesField v-model="story.categories" />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="story.notificationCTA"
            :rules="notificationCTARules"
            label="Notification message"
            hint="This text will be used to remind users of this quiz. It has a maximum length of 150"
            required
            counter
          ></v-text-field>
        </v-col>
      </v-row>

      <v-select
        v-model="story.notRelevantForlabels"
        label="Not relevant for labels"
        multiple
        clearable
        class="mt-4"
        hide-details="auto"
        :style="{ background: 'white' }"
        :items="labels.map((q) => ({ title: q.value, value: q }))"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>
      <v-select
        v-model="story.initiativeOfTheDay"
        label="Initiative of the day"
        clearable
        class="mt-4"
        hide-details="auto"
        :style="{ background: 'white' }"
        :items="initiatives.map((q) => ({ text: q.title, value: q }))"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>

      <MediaList v-model="story" :required="[MediaPurpose.BackgroundImage]" />
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" block>{{ action }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
